// Phase 10 — useTemplates hook.
//
// Read-only consumer of signal_templates, chain_templates,
// and report_templates tables.

import { useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

// ─── Types ─────────────────────────────────────────────────────

export interface SignalTemplate {
  id: string;
  signal_type: string;
  label: string;
  description: string | null;
  market_type: string;
  default_priority: string;
  required_fields: readonly string[];
  optional_fields: readonly string[];
  auto_approve: boolean;
  confidence_min: number;
  sort_order: number;
  active: boolean;
  created_at: string;
}

export interface ChainTemplate {
  id: string;
  chain_type: string;
  label: string;
  description: string | null;
  max_legs: number;
  market_types: readonly string[];
  strategy: string | null;
  risk_profile: string;
  sort_order: number;
  active: boolean;
  created_at: string;
}

export interface ReportTemplate {
  id: string;
  report_type: string;
  label: string;
  description: string | null;
  sections: unknown[];
  frequency: string;
  auto_generate: boolean;
  sort_order: number;
  active: boolean;
  created_at: string;
}

export interface UseTemplatesState<T> {
  templates: readonly T[];
  loading: boolean;
  error: string | null;
}

// ─── Parsers ───────────────────────────────────────────────────

function asStr(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function asNum(v: unknown): number {
  if (v === null || v === undefined) return 0;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function parseSignal(row: Record<string, unknown>): SignalTemplate {
  return {
    id: String(row.id ?? ""),
    signal_type: String(row.signal_type ?? ""),
    label: String(row.label ?? ""),
    description: asStr(row.description),
    market_type: String(row.market_type ?? "football"),
    default_priority: String(row.default_priority ?? "medium"),
    required_fields: Array.isArray(row.required_fields) ? (row.required_fields as string[]) : [],
    optional_fields: Array.isArray(row.optional_fields) ? (row.optional_fields as string[]) : [],
    auto_approve: row.auto_approve === true,
    confidence_min: asNum(row.confidence_min),
    sort_order: asNum(row.sort_order),
    active: row.active !== false,
    created_at: String(row.created_at ?? ""),
  };
}

function parseChain(row: Record<string, unknown>): ChainTemplate {
  return {
    id: String(row.id ?? ""),
    chain_type: String(row.chain_type ?? ""),
    label: String(row.label ?? ""),
    description: asStr(row.description),
    max_legs: asNum(row.max_legs),
    market_types: Array.isArray(row.market_types) ? (row.market_types as string[]) : [],
    strategy: asStr(row.strategy),
    risk_profile: String(row.risk_profile ?? "medium"),
    sort_order: asNum(row.sort_order),
    active: row.active !== false,
    created_at: String(row.created_at ?? ""),
  };
}

function parseReport(row: Record<string, unknown>): ReportTemplate {
  return {
    id: String(row.id ?? ""),
    report_type: String(row.report_type ?? ""),
    label: String(row.label ?? ""),
    description: asStr(row.description),
    sections: Array.isArray(row.sections) ? (row.sections as unknown[]) : [],
    frequency: String(row.frequency ?? "on_demand"),
    auto_generate: row.auto_generate === true,
    sort_order: asNum(row.sort_order),
    active: row.active !== false,
    created_at: String(row.created_at ?? ""),
  };
}

// ─── Generic fetcher ───────────────────────────────────────────

function useTemplateTable<T>(
  tableName: string,
  parser: (row: Record<string, unknown>) => T,
): UseTemplatesState<T> {
  const { user } = useAuthSession();
  const [templates, setTemplates] = useState<readonly T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setTemplates([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      const resp = await (
        client.from(tableName as never) as unknown as {
          select: (cols: string) => {
            order: (col: string, opts: { ascending: boolean }) => Promise<{
              data: Record<string, unknown>[] | null;
              error: { message: string } | null;
            }>;
          };
        }
      )
        .select("*")
        .order("sort_order", { ascending: true });

      if (cancelled) return;

      if (resp.error) {
        setError(resp.error.message);
        setTemplates([]);
        setLoading(false);
        return;
      }

      setTemplates(
        (resp.data ?? [])
          .filter((r): r is Record<string, unknown> => r !== null)
          .map(parser),
      );
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [user, tableName, parser]);

  return useMemo(
    () => ({ templates, loading, error }),
    [templates, loading, error],
  );
}

// ─── Exported hooks ────────────────────────────────────────────

export function useSignalTemplates(): UseTemplatesState<SignalTemplate> {
  return useTemplateTable("signal_templates", parseSignal);
}

export function useChainTemplates(): UseTemplatesState<ChainTemplate> {
  return useTemplateTable("chain_templates", parseChain);
}

export function useReportTemplates(): UseTemplatesState<ReportTemplate> {
  return useTemplateTable("report_templates", parseReport);
}
